import { useState } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';

/**
 * Custom hook for managing coach feedback state and operations
 */
export const useCoachFeedback = (videoRef, playerRef) => {
  const [coachFeedbackText, setCoachFeedbackText] = useState('');
  const [coachFeedbacks, setCoachFeedbacks] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');

  // Audio recording state (mirrors CreateFeedback.jsx behavior)
  const [audioComment, setAudioComment] = useState(null); // base64 MP3 data URL
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [mediaStream, setMediaStream] = useState(null);
  const ffmpeg = new FFmpeg();
  const maxRecordingDuration = parseInt(process.env.REACT_APP_MAX_RECORDING_DURATION) || 4;

  const handleSaveCoachFeedback = () => {
    const text = (coachFeedbackText || '').trim();
    const hasAudio = !!audioComment;
    const hasText = !!text;
    if (!hasText && !hasAudio) return;
    // derive current playback time from native video or video.js
    let tSec = 0;
    try {
      const el = videoRef?.current;
      const player = playerRef?.current;
      if (el && typeof el.currentTime === 'number') tSec = Number(el.currentTime) || 0;
      else if (player && typeof player.currentTime === 'function') tSec = Number(player.currentTime()) || 0;
    } catch {}
    const item = { id: `${Date.now()}`, text: hasText ? text : '', timeSec: Number(tSec), audio: hasAudio ? audioComment : null };
    setCoachFeedbacks(prev => [item, ...prev]);
    setCoachFeedbackText('');
    setAudioComment(null);
    setAudioChunks([]);
  };

  const handleDeleteFeedback = (id) => {
    setCoachFeedbacks(prev => prev.filter(it => it.id !== id));
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (editingId === id) {
      setEditingId(null);
      setEditingText('');
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditingText(item.text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const handleCommitEdit = () => {
    const txt = (editingText || '').trim();
    if (!editingId) return;
    if (!txt) { handleCancelEdit(); return; }
    setCoachFeedbacks(prev => prev.map(it => it.id === editingId ? { ...it, text: txt } : it));
    setEditingId(null);
    setEditingText('');
  };

  // --- Voice recording handlers (ported from CreateFeedback.jsx) ---
  const stopMediaTracks = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
    }
    setMediaStream(null);
  };

  const handleStartRecording = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          setMediaStream(stream);
          const recorder = new MediaRecorder(stream);
          setMediaRecorder(recorder);
          const chunks = [];

          recorder.ondataavailable = (event) => {
            chunks.push(event.data);
          };

          recorder.onstop = async () => {
            stopMediaTracks();
            if (!recorder.exceededMaxDuration) {
              setAudioChunks(chunks);
              const audioBlob = new Blob(chunks, { type: 'audio/wav' });
              const base64MP3 = await convertWavToMp3(audioBlob);
              setAudioComment(base64MP3);
            }
          };

          recorder.start();
          setIsRecording(true);

          setTimeout(() => {
            if (recorder.state === 'recording') {
              recorder.exceededMaxDuration = true;
              recorder.stop();
              setIsRecording(false);
              setAudioComment(null);
              setAudioChunks([]);
              // Intentionally no toast here in hook; UI can inform user
            }
          }, (maxRecordingDuration + 1) * 1000);
        })
        .catch(() => {
          // Optionally surface error to UI via another state if needed
        });
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
      stopMediaTracks();
    }
  };

  const convertWavToMp3 = async (audioBlob) => {
    if (!ffmpeg.loaded) {
      await ffmpeg.load();
    }
    const fileData = await audioBlob.arrayBuffer();
    await ffmpeg.writeFile('input.wav', new Uint8Array(fileData));
    await ffmpeg.exec(['-i', 'input.wav', '-b:a', '64k', 'output.mp3']);
    const mp3Data = await ffmpeg.readFile('output.mp3');
    const mp3Blob = new Blob([mp3Data], { type: 'audio/mp3' });
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(mp3Blob);
      reader.onloadend = () => resolve(reader.result);
    });
  };

  const handleDeleteAudio = () => {
    setAudioComment(null);
    setAudioChunks([]);
  };

  return {
    coachFeedbackText,
    setCoachFeedbackText,
    coachFeedbacks,
    expandedIds,
    editingId,
    editingText,
    setEditingText,
    handleSaveCoachFeedback,
    handleDeleteFeedback,
    toggleExpand,
    handleStartEdit,
    handleCancelEdit,
    handleCommitEdit,
    // audio feedback
    audioComment,
    isRecording,
    handleStartRecording,
    handleStopRecording,
    handleDeleteAudio,
  };
};
